import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../firebase';

/**
 * Client-side early-out check and fire-and-forget trigger for the SSI
 * completion (MDT information available) email.
 *
 * Called from the CI AND SSI review exits on both pathways (adult RD_CI.js /
 * RD_SSI.js, CYP CypAdhdReviewDocument.js). The server-side callable
 * (`sendSSICompletionEmail`) owns the decision about whether the MDT
 * information is ready yet, so firing from either exit is always safe:
 *
 *   - Orgs where one clinician does CI+SSI (TOC, WLC; stamped
 *     ddFlow.mdtInfoEmailGate = bothReportsApproved) and CYP ADHD: the
 *     email sends once BOTH reports are approved, so whichever exit comes
 *     second sends it and the other is a no-op.
 *   - Orgs where one clinician does the CI and another the SSI (Phoenix,
 *     SLaM, 3 Borough, Urban ADHD; key absent): the email sends as soon as
 *     the SSI report is approved. A CI exit then only matters if the SSI
 *     was already approved and the email has not gone out yet (e.g. the
 *     MDT reviewer was assigned after the SSI exit).
 *
 * The server also does the pathway branching (separate org config key,
 * where the approval flags live, who is named in the email); the client only
 * performs a cheap dedup early-out to avoid a redundant Cloud Function
 * invocation when the stamp is already present.
 *
 * Adult patients use the `ssiCompletionEmailSent` stamp; CYP patients
 * use `cypSsiCompletionEmailSent`. We check both: a CYP patient never
 * has the adult stamp and vice versa, so checking both is safe and
 * avoids the trigger needing to know which pathway the patient is on.
 *
 * @param {string} patientId - The patient's user ID
 * @param {string} clinicianId - The clinician's user ID
 */
export const TriggerSSICompletionEmail = async (patientId, clinicianId) => {
    if (!patientId) return;

    try {
        console.log(`[NotificationFunctions] Checking SSI completion email for patient: ${patientId}`);

        // Get patient data with a single Firestore read
        const patientDoc = await getDoc(doc(db, 'patients', patientId));
        if (!patientDoc.exists()) return;

        const patientData = patientDoc.data();

        // Skip if notification already sent (check both adult and CYP stamps)
        if (patientData.ssiCompletionEmailSent || patientData.cypSsiCompletionEmailSent) {
            console.log('Notification already sent for SSI completion email');
            return;
        }
        
        console.log('Triggering notification...');
        // Fire and forget - don't await this call
        httpsCallable(functions, 'sendSSICompletionEmail')({ patientId, clinicianId })
            .then(() => console.log('SSI completion email sent'))
            .catch(err => console.error('Error sending SSI completion email:', err));

    } catch (error) {
        console.error('Error checking SSI completion email:', error);
    }
};